import { ContractState, ContractViewModel, ServiceKind } from '@ail/contracts-api'
import { VehicleOrderStatus } from '@ail/orders-api'
import { skipToken } from '@reduxjs/toolkit/query'
import ExcellentExport, { ConvertOptions } from 'excellentexport'
import { useCallback, useMemo, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { NavLink, Redirect, useLocation, useParams } from 'react-router-dom'
import { Arr } from '../../helpers/Arr'
import { downloadFile } from '../../helpers/FileDownload'
import { FrontendSettings } from '../../helpers/FrontendSettings'
import { hasTimeElapsed, printDate } from '../../helpers/Utils'
import { useAuth } from '../../hooks/useAuth'
import { useSettings } from '../../hooks/useSettings'
import {
  useGetContractQuery,
  useGetMonthlyCostsQuery,
  useGetMonthlyDistancesQuery,
  useGetMonthlyRevenuesQuery,
} from '../../redux/api/contractsApi'
import { useGetAllCustomersQuery, useGetAllUnitsQuery, useGetTenantsQuery } from '../../redux/api/customerApi'
import { useGetContractDocumentsQuery, useLazyDownloadDocumentQuery } from '../../redux/api/documentsApi'
import {
  useGetOrderQuery,
  useGetOrdersFormsQuery,
  useGetVehicleOrderQuery,
  useSearchOrdersQuery,
} from '../../redux/api/ordersApi'
import { noCostCenter } from '../../types/constants'
import { FrontendSettingsKeys } from '../../types/enums'
import { Orders } from '../../utils/orders'
import IdIsCopied from '../../helpers/IdIsCopied'
import Alert from '../common/Alert/Alert'
import { ActiveServiceDetails } from './ActiveServiceDetails/ActiveServiceDetails'
import './ContractDetails.scss'
import { CostDetails } from './CostDetails/CostDetails'
import { DocumentDetails } from './DocumentDetails/DocumentDetails'
import { DriverDetails } from './DriverDetails/DriverDetails'
import { DurationAndMileageDetails } from './DurationAndMileageDetails/DurationAndMileageDetails'
import { FinancialDetails } from './FinancialDetails/FinancialDetails'
import DataSecurityPopup from './Popups/DataSecurityPopup'
import { RequestDetails } from './RequestDetails/RequestDetails'
import { StatusDetails } from './StatusDetails/StatusDetails'
import { VehicleDetails } from './VehicleDetails/VehicleDetails'
import { VehicleOrderDetails } from './VehicleOrderDetails/VehicleOrderDetails'
import { VehicleWrappingDetails } from './VehicleWrappingDetails/VehicleWrappingDetails'
import { useContractSupport } from '../../hooks/useContractSupport'
import { Matomo } from '../../helpers/Matomo'
import { FuelCardDetails } from './FuelCardDetails/FuelCardDetails'
import { Loader } from '../common/Loader/Loader'

export function ContractDetails() {
  const [frozenWarning, setFrozenWarning] = useState<string>()
  const [currentContract, setCurrentContract] = useState<ContractViewModel>()
  const [failedDocument, setFailedDocument] = useState<string>()
  const [dataSecurityConfirmation, setDataSecurityConfirmation] = useState(false)
  const [copiedToClipboard, setCopiedToClipboard] = useState(false)
  const [costsInterval, setCostsInterval] = useState<{ from: Date; to: Date }>()
  const [costsIntervalBounds, setCostsIntervalBounds] = useState<{ from: Date; to: Date }>()
  const { id: contractId } = useParams() as { id: string }
  const { user } = useAuth()
  const { t, i18n } = useTranslation('common')
  const location = useLocation()

  const [contractCostsInterval, saveContractCostsInterval] = useSettings('contractCostsInterval', {
    remote: false,
  })
  const [excludeRestrictedDocuments, saveExcludeRestrictedDocuments] = useSettings('excludeRestrictedDocuments')
  const [expiringDaysFilter] = useSettings('expiringDaysFilter')

  const excludeRestricted = excludeRestrictedDocuments === 'y' || !user?.isInternalUser
  const canSeeExtraInfo = useMemo(() => {
    if (user?.isAdmin || user?.isInternalUser) return true
    const canSee = user?.permissions?.includes('costs.view') || user?.permissions?.includes('documents.view')
    return !!canSee
  }, [user])
  const { setContractSupportRef } = useContractSupport()

  const contract = useGetContractQuery(contractId)
  const customers = useGetAllCustomersQuery()
  const units = useGetAllUnitsQuery()
  const tenants = useGetTenantsQuery()

  const contractDocuments = useGetContractDocumentsQuery({ contractId, excludeRestricted })
  const contractRequest = useGetOrderQuery(currentContract?.requestId ?? skipToken)
  const contractOrders = useSearchOrdersQuery(
    user?.isInternalUser && currentContract?.reference
      ? { dataKey: 'OV001', dataValue: currentContract.reference }
      : skipToken
  )
  const { currentData: vehicleOrder } = useGetVehicleOrderQuery(currentContract?.requestId ?? skipToken)

  const forms = useGetOrdersFormsQuery()
  const monthlyDistances = useGetMonthlyDistancesQuery({ contractId })
  const monthlyCosts = useGetMonthlyCostsQuery({ contractId, excludeRestricted })
  const monthlyRevenues = useGetMonthlyRevenuesQuery({ contractId, excludeRestricted })

  const [downloadDocument] = useLazyDownloadDocumentQuery()

  const ODpage = location.pathname.includes('orderAndDelivery')
  const loading = contract.isFetching || customers.isFetching || units.isFetching || forms.isFetching

  if (currentContract !== contract.currentData) {
    setCurrentContract(contract.currentData)
    if (contract.currentData) {
      const contractStart = new Date(contract.currentData.startDate)
      const from =
        contractCostsInterval?.contractId === contract.currentData.id
          ? new Date(contractCostsInterval.from)
          : contractStart
      const to =
        contractCostsInterval?.contractId === contract.currentData.id ? new Date(contractCostsInterval.to) : new Date()
      setCostsInterval({ from, to })
      setCostsIntervalBounds({ from: contractStart, to: new Date() })
    }
  }

  const isOrderFrozen = useMemo(() => {
    return (
      !!vehicleOrder?.freeze &&
      hasTimeElapsed(vehicleOrder.freeze, 0) &&
      //!vehicleOrder.earliestDelivery && maybe needed
      vehicleOrder.status !== VehicleOrderStatus.Delivered &&
      vehicleOrder.status !== VehicleOrderStatus.Delivering
    )
  }, [vehicleOrder])

  const currentContractCustomer = useMemo(() => {
    if (!contract.currentData || !customers.currentData) {
      return undefined
    }
    return customers.currentData.find((c) => c.value && contract.currentData?.owner?.startsWith(c.value))?.customer
  }, [contract.currentData, customers.currentData])

  const currentContractCostCenter = useMemo(() => {
    if (!contract.currentData || !units.currentData) {
      return undefined
    }
    return units.currentData.find((u) => u.value && contract.currentData?.owner === u.value)?.unit
  }, [contract.currentData, units.currentData])

  const contractOrderForms = useMemo(() => {
    return forms.currentData?.filter((f) => contractOrders.currentData?.some((o) => o.formId === f.id)) ?? []
  }, [contractOrders.currentData, forms.currentData])

  const quickActions = useMemo(() => {
    if (!currentContract || !forms.currentData) {
      return []
    }

    const filteredForms = forms.currentData.filter((form) =>
      Orders.isFormAvailableForContractQuickAction(
        form,
        currentContract,
        Number(expiringDaysFilter),
        user,
        tenants.currentData,
        customers.currentData
      )
    )
    const actions = [
      {
        form: undefined,
        label: t('SUPPORT_CONTRACT'),
      },
      ...filteredForms.map((f) => {
        return { form: f, label: Orders.getFormTitle(f, i18n.language) }
      }),
    ].sort((a, b) => a.label.localeCompare(b.label))
    return actions
  }, [
    currentContract,
    customers.currentData,
    forms.currentData,
    i18n.language,
    t,
    tenants.currentData,
    user,
    expiringDaysFilter,
  ])

  const contractDistances = useMemo(() => {
    if (!costsInterval || !monthlyDistances.currentData) {
      return []
    }

    const from = new Date(costsInterval.from.getFullYear(), costsInterval.from.getMonth())
    const to = new Date(costsInterval.to.getFullYear(), costsInterval.to.getMonth())

    const filtered = monthlyDistances.currentData.filter((d) => {
      const date = new Date(d.year, d.month - 1)
      return from <= date && date <= to
    })

    return Arr.sort(filtered, (d) => new Date(d.year, d.month - 1))
  }, [monthlyDistances.currentData, costsInterval])

  const actualExpectedDistances = useMemo(() => {
    const result: {
      totalExpected: number
      totalActual: number
      totalDifference: number
    } = {
      totalExpected: 0,
      totalActual: 0,
      totalDifference: 0,
    }

    for (const distance of contractDistances) {
      result.totalActual += distance.actualMonthDistance ?? 0
      result.totalExpected += distance.expectedMonthDistance ?? 0
    }

    result.totalDifference = result.totalExpected - result.totalActual

    return result
  }, [contractDistances])

  const availableCostKinds = useMemo(() => {
    const costKinds = monthlyCosts.currentData?.map((c) => c.serviceKind) ?? []
    const revenueKinds = monthlyRevenues.currentData?.map((c) => c.serviceKind) ?? []
    return Array.from(new Set([...costKinds, ...revenueKinds]))
  }, [monthlyCosts.currentData, monthlyRevenues.currentData])

  const actualExpectedCosts = useMemo(() => {
    const result: {
      totalExpected: number
      totalActual: number
      totalDifference: number
      byKind: [ServiceKind, number, number, number][]
    } = {
      totalExpected: 0,
      totalActual: 0,
      totalDifference: 0,
      byKind: [],
    }

    if (!monthlyCosts.currentData || !monthlyRevenues.currentData || !costsInterval) {
      return result
    }

    const start = new Date(costsInterval.from.getFullYear(), costsInterval.from.getMonth())
    const end = new Date(costsInterval.to.getFullYear(), costsInterval.to.getMonth())

    const mergedCostKinds = availableCostKinds.reduce((result, current) => {
      const kind = current === ServiceKind.General ? ServiceKind.Reestablishment : current
      const search: ServiceKind[] = result[kind] ?? []
      search.push(current)
      result[kind] = search
      return result
    }, {} as Partial<Record<ServiceKind, ServiceKind[]>>)

    for (const key of Object.keys(mergedCostKinds)) {
      const kind = key as ServiceKind
      const searchKinds = mergedCostKinds[kind] ?? []
      const costs = monthlyCosts.currentData.filter((c) => {
        const date = new Date(c.year, c.month - 1)
        const amount = c.externalAmount
        return searchKinds.includes(c.serviceKind) && !!amount && start <= date && date <= end
      })

      const revenues = monthlyRevenues.currentData.filter((r) => {
        const date = new Date(r.year, r.month - 1)
        return searchKinds.includes(r.serviceKind) && r.amount && start <= date && date <= end
      })

      const actual = costs.reduce((sum, c) => sum + (c.externalAmount ?? 0), 0)
      const expected = revenues.reduce((sum, r) => sum + (r.amount ?? 0), 0)
      const difference = actual - expected

      if (expected || actual) {
        result.totalActual += actual
        result.totalExpected += expected
        result.totalDifference += difference
        result.byKind.push([kind, actual, expected, difference])
      }
    }

    return result
  }, [monthlyCosts.currentData, monthlyRevenues.currentData, availableCostKinds, costsInterval])

  const showCosts = useMemo(() => {
    return !currentContract?.startDate.includes('1800') && !currentContract?.endDate.includes('3000')
  }, [currentContract])

  const saveCostCenterInLocalStorage = () => {
    const key =
      currentContract?.state !== ContractState.Initialized
        ? FrontendSettingsKeys.costCenterPreselection
        : FrontendSettingsKeys.costCenterPreselectionOD

    FrontendSettings.set(key, currentContractCostCenter?.name ?? noCostCenter)
  }

  const print = (e: React.MouseEvent<HTMLAnchorElement, MouseEvent>) => {
    e.preventDefault()
    window.print()
  }

  const toFixed = (value: number) => {
    return value.toFixed(2).replace('.00', '')
  }

  const exportWYSIWYG = (dataSecurityConfirmed = false) => {
    if (!currentContract) {
      return
    }

    if (!dataSecurityConfirmed) {
      setDataSecurityConfirmation(true)
      return
    }

    Matomo.pushEvent('DataProtection', 'Export', 'Contract', window.location.href)

    setTimeout(() => {
      const a = document.createElement('a')
      document.body.appendChild(a)
      const data = []
      const headers: string[] = []
      headers.push(t('COST_CENTER'))
      headers.push(t('CONTRACT'))
      headers.push(t('CONTRACT_DURATION'))
      headers.push(t('LICENSE_NUMBER'))
      headers.push(t('DRIVER'))
      headers.push(t('VEHICLE'))
      headers.push(t('CONTRACT_DISTANCE'))
      headers.push(t('TYPE'))
      headers.push(t('MILEAGES'))
      actualExpectedCosts.byKind.forEach((cost) => {
        headers.push(`${t(cost[0].toUpperCase())}`)
      })
      headers.push(t('TOTAL'))
      data.push(headers)

      const cellsForExpected: string[] = []
      cellsForExpected.push(`${currentContractCostCenter?.name || t('NO_COST_CENTER')}`)
      cellsForExpected.push(`${currentContract.reference}`)
      cellsForExpected.push(
        `${currentContract.duration} ${t('MONTHS')} (${printDate(currentContract.startDate)} - ${printDate(
          currentContract.endDate
        )})`
      )
      cellsForExpected.push(`${currentContract.vehicle?.licensePlate?.licenseNumber}`)
      cellsForExpected.push(`${currentContract.vehicle?.driver?.displayName}`)
      cellsForExpected.push(`${currentContract.vehicle?.description}`)
      cellsForExpected.push(`${currentContract.distance}`)
      cellsForExpected.push(`${t('EXPECTED')}`)
      cellsForExpected.push(`${actualExpectedDistances.totalExpected}`)
      actualExpectedCosts.byKind.forEach((cost) => {
        cellsForExpected.push(`${toFixed(cost[2])}`)
      })
      cellsForExpected.push(`${toFixed(actualExpectedCosts.totalExpected)}`)
      data.push(cellsForExpected)

      const cellsForActual: string[] = []
      cellsForActual.push('', '', '', '', '', '', '')
      cellsForActual.push(`${t('ACTUAL')}`)
      cellsForActual.push(`${actualExpectedDistances.totalActual}`)
      actualExpectedCosts.byKind.forEach((cost) => {
        cellsForActual.push(`${toFixed(cost[1])}`)
      })
      cellsForActual.push(`${toFixed(actualExpectedCosts.totalActual)}`)
      data.push(cellsForActual)

      const cellsForDifference: string[] = []
      cellsForDifference.push('', '', '', '', '', '', '')
      cellsForDifference.push(`${t('DIFFERENCE')}`)
      cellsForDifference.push(`${actualExpectedDistances.totalActual - actualExpectedDistances.totalExpected}`)
      actualExpectedCosts.byKind.forEach((cost) => {
        cellsForDifference.push(`${toFixed(cost[3])}`)
      })
      cellsForDifference.push(`${toFixed(actualExpectedCosts.totalActual - actualExpectedCosts.totalExpected)}`)
      data.push(cellsForDifference)

      const worksheet = `${currentContractCustomer?.name} ${currentContract.reference}`

      const options: ConvertOptions = {
        anchor: a,
        format: 'xlsx',
        filename: `${worksheet.replace(/\s/g, '-')}-Cost-Detail-${currentContract.startDate}-${
          currentContract.endDate
        }-${new Date().getTime()}`,
      }

      const sheet = {
        name: '1',
        from: {
          array: data,
          arrayHasHeader: true,
        },
      }
      ExcellentExport.convert(options, [sheet])

      downloadFile({ a })
    }, 100)
  }

  const downloadContractDocument = async (documentId: string, filename: string) => {
    try {
      const response = await downloadDocument({ documentId }).unwrap()
      const file = new Blob([response.data], { type: 'application/octet-stream' })
      const fileURL = URL.createObjectURL(file)
      downloadFile({
        fileURL,
        filename,
      })
    } catch {
      setFailedDocument(filename)
    }
  }

  const copyToClipboard = (value?: string) => {
    if (!value) {
      return
    }

    navigator.clipboard
      .writeText(value)
      .then(() => {
        setCopiedToClipboard(true)
        setTimeout(() => {
          setCopiedToClipboard(false)
        }, 5000)
      })
      .catch(() => {
        setCopiedToClipboard(false)
      })
  }

  const handleActionClick = useCallback(
    (e: React.MouseEvent) => {
      if (isOrderFrozen) {
        e.preventDefault()
        setFrozenWarning(t('FROZEN_CONTRACT_REQUEST_WARNING'))
      }
    },
    [isOrderFrozen, t]
  )

  const openContractSupport = useCallback(() => {
    setContractSupportRef(
      isOrderFrozen
        ? [currentContract?.reference ?? '', t('FROZEN_CONTRACT_SUPPORT_PLACEHOLDER')]
        : currentContract?.reference ?? ''
    )
  }, [currentContract?.reference, isOrderFrozen, setContractSupportRef, t])

  if (contract.error) {
    return <Redirect to={'/contractNotFound'} />
  }

  return (
    <>
      <Loader blocking={loading} style={{ overflow: 'hidden' }}>
        <ul className={excludeRestricted ? 'breadcrumbs' : 'breadcrumbs eyeOk'}>
          <li>
            <NavLink
              to={`/${
                currentContract?.state === ContractState.Initialized ? 'orderAndDelivery/' : 'generalContracts/'
              }${currentContractCustomer?.id}`}
              exact={true}
            >
              <span>{currentContractCustomer?.name}</span>
            </NavLink>
          </li>
          <li>
            <NavLink
              onClick={saveCostCenterInLocalStorage}
              to={`/${
                currentContract?.state === ContractState.Initialized ? 'orderAndDelivery/' : 'generalContracts/'
              }${currentContractCustomer?.id}`}
              exact={true}
            >
              <span>{currentContractCostCenter?.name || t('NO_COST_CENTER')}</span>
            </NavLink>
          </li>
          <li>
            <span>
              {currentContract?.reference}
              {!!currentContract?.id &&
                [ContractState.Running, ContractState.Extended, ContractState.Initialized].includes(
                  currentContract?.state
                ) && (
                  <div className="quickActions">
                    <div className="actionTrigger">
                      {t('ACTIONS')}
                      <i className="icon-actions" />
                      <i className="icon-bottom" />
                    </div>
                    <ul className="quickActionsList">
                      {quickActions.map(({ form: f, label }, i) => {
                        if (label === t('SUPPORT_CONTRACT')) {
                          return (
                            <li key={'contractIssue'}>
                              <a
                                onClick={() =>
                                  isOrderFrozen
                                    ? setFrozenWarning(t('FROZEN_CONTRACT_REQUEST_WARNING'))
                                    : openContractSupport()
                                }
                              >
                                <i className={'icon-help'} /> {t('SUPPORT_CONTRACT')}
                              </a>
                            </li>
                          )
                        }
                        if (!f) return
                        return (
                          <li key={i}>
                            <NavLink
                              to={`/requests/new/${f.id}/contract/${contractId}`}
                              exact={true}
                              onClick={(e) => handleActionClick(e)}
                            >
                              <i className={f.icon || 'icon-order'} /> {label || f.id}
                            </NavLink>
                          </li>
                        )
                      })}
                    </ul>
                  </div>
                )}
            </span>
          </li>
          <li>
            <span>
              {(user?.isAdmin || user?.isInternalUser) && (
                <i
                  className={excludeRestricted ? 'icon-hide' : 'icon-show'}
                  onClick={() => saveExcludeRestrictedDocuments(excludeRestricted ? 'n' : 'y')}
                />
              )}{' '}
            </span>
          </li>
        </ul>
        {!ODpage && !loading && (
          <>
            <div className={excludeRestricted ? `contractFilters` : `contractFilters eyeOk`}>
              <div className="leftFilters">
                <a onClick={(e) => print(e)}>
                  <i className="icon-print" /> <span>{t('PRINT')}</span>
                </a>
                <a onClick={() => exportWYSIWYG()}>
                  <i className="icon-excel" /> <span>{t('EXPORT_CONTRACT_TO_EXCEL')}</span>
                </a>
              </div>
            </div>
          </>
        )}
        <div className="row">
          <div className="col-12 col-md-6 col-sm-6">
            <div className="row">
              <div className="col-12 col-md-7 col-sm-12">
                <VehicleDetails vehicle={currentContract?.vehicle} onCopy={copyToClipboard} />
                {user?.isInternalUser && !excludeRestricted && <FinancialDetails contract={currentContract} />}
              </div>
              <div className="col-12 col-md-5 col-sm-12">
                <DriverDetails contract={currentContract} onCopy={copyToClipboard} />
                <StatusDetails contract={currentContract} onCopy={copyToClipboard} />
                <FuelCardDetails contract={currentContract} />
                {user?.isInternalUser && !excludeRestricted && (
                  <VehicleWrappingDetails vehicle={currentContract?.vehicle} />
                )}
              </div>
            </div>
          </div>
          <div className="col-12 col-md-6 col-sm-6">
            {!!vehicleOrder && (
              <VehicleOrderDetails
                vehicleOrder={vehicleOrder}
                show={!!vehicleOrder && vehicleOrder.status !== VehicleOrderStatus.Delivered}
              />
            )}
            <DurationAndMileageDetails
              contract={currentContract}
              expectedCosts={actualExpectedCosts.totalExpected}
              actualCosts={actualExpectedCosts.totalActual}
              expectedDistance={actualExpectedDistances.totalExpected}
              actualDistance={actualExpectedDistances.totalActual}
              excludeRestricted={excludeRestricted}
              show={!vehicleOrder || vehicleOrder.status === VehicleOrderStatus.Delivered}
            />
            {user?.isInternalUser && !excludeRestricted && <ActiveServiceDetails contract={currentContract} />}
            {user?.isInternalUser && !!contractOrders.currentData?.length && !excludeRestricted && (
              <RequestDetails orders={contractOrders.currentData} forms={contractOrderForms} />
            )}
          </div>
        </div>
        {!!currentContract && canSeeExtraInfo && (
          <div className="row">
            <div className="col-12 col-sm-12 col-md-6 col-lg-6">
              <div className="detailContentArea">
                {!!currentContract && (
                  <DocumentDetails
                    contract={currentContract}
                    documents={contractDocuments.currentData ?? []}
                    request={contractRequest.currentData}
                    onDocumentDownload={downloadContractDocument}
                    onRequestDownload={(r) => Orders.exportOrder(r, forms.currentData ?? [], i18n.language, t)}
                  />
                )}
              </div>
            </div>
            {!!costsInterval && showCosts && currentContract && canSeeExtraInfo && (
              <div className="col-12 col-sm-12 col-md-6 col-lg-6">
                <CostDetails
                  costs={actualExpectedCosts}
                  interval={costsInterval}
                  range={costsIntervalBounds}
                  onIntervalChange={(from, to) => {
                    setCostsInterval({ from, to })
                    saveContractCostsInterval({
                      from: from.toJSON(),
                      to: to.toJSON(),
                      contractId: currentContract.id,
                    })
                  }}
                  excludeRestricted={excludeRestricted}
                />
              </div>
            )}
          </div>
        )}
      </Loader>
      <Alert
        onClose={() => setFailedDocument(undefined)}
        visible={!!failedDocument}
        title={t('ERROR')}
        message={`${t('DOWNLOAD_OF_DOCUMENT')} ${failedDocument} ${t('FAILED')}...`}
      />
      <Alert
        visible={!!frozenWarning}
        onClose={() => {
          setFrozenWarning(undefined)
          openContractSupport()
        }}
        title={t('WARNING')}
        message={frozenWarning ?? ''}
      />
      <DataSecurityPopup
        onConfirm={() => {
          setDataSecurityConfirmation(false)
          exportWYSIWYG(true)
        }}
        onClose={() => setDataSecurityConfirmation(false)}
        visible={dataSecurityConfirmation}
      />
      {copiedToClipboard && <IdIsCopied value={true} idCopied={true} />}
    </>
  )
}
